import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// GA4: load gtag.js once and configure with send_page_view:false so the
// SPA pageview tracker (src/components/GA4PageView.jsx) becomes the single
// source of truth for pageviews. No double-count on initial load + every
// React Router navigation gets tracked. Silent no-op when REACT_APP_GA4_ID
// isn't set.
if (process.env.REACT_APP_GA4_ID) {
  const gaId = process.env.REACT_APP_GA4_ID;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', gaId, { send_page_view: false });
}

// Sentry: only initialise when a DSN is present. Lets local + preview builds
// run without polluting the production project's quota.
//
// Replay integration is heavy (~80KB minified) and only useful inside the
// authenticated app where reproducing bugs matters. Skip it on marketing
// routes (/, /pricing, /payroll-software, /vs/*, etc.) so landing-page
// visitors don't carry the cost. Errors and traces still get captured
// everywhere via the always-on browserTracingIntegration.
if (process.env.REACT_APP_SENTRY_DSN) {
  const isAppRoute = typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/dashboard');

  const integrations = [Sentry.browserTracingIntegration()];
  if (isAppRoute) {
    integrations.push(Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }));
  }

  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
    release: process.env.REACT_APP_SENTRY_RELEASE,
    integrations,
    tracesSampleRate: 0.1,
    // Session replays: 10% of normal sessions, 100% on error. Only takes
    // effect when the replay integration above is loaded (dashboard only).
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
